<template>
  <section class="candidate-queue">
    <div class="candidate-queue__toolbar">
      <v-text-field
        v-model="search"
        label="Marka veya kampanya ara"
        dense
        outlined
        hide-details
        clearable
        @input="searchLater"
      />
      <v-autocomplete
        v-model="offerRefId"
        :items="brands"
        item-text="brandName"
        item-value="offerRefId"
        label="Marka"
        :loading="brandsLoading"
        clearable
        dense
        outlined
        hide-details
        no-data-text="Marka bulunamadı"
        @change="reset"
      />
      <v-select
        v-model="status"
        :items="statuses"
        label="Kayıtlar"
        dense
        outlined
        hide-details
        @change="statusChanged"
      />
      <v-btn
        icon
        aria-label="Yenile"
        title="Yenile"
        :disabled="loading"
        @click="reload"
      >
        <v-icon>{{ icons.mdiRefresh }}</v-icon>
      </v-btn>
      <v-checkbox
        v-model="readyOnly"
        label="Bu sayfada yayına hazır"
        hide-details
        class="mt-0"
        @change="selected = []"
      />
      <v-btn
        v-if="status === 'PENDING'"
        color="primary"
        :disabled="!approvalRows.length || busy || loading"
        @click="confirm = true"
      >
        <v-icon left>
          {{ icons.mdiCheckAll }}
        </v-icon> Seçilenleri onayla ({{ approvalRows.length }})
      </v-btn>
      <span
        v-if="selected.length"
        class="text-caption"
        role="status"
      >
        {{ selected.length }} seçili · {{ approvalRows.length }} yayına hazır · {{ selected.length - approvalRows.length }} inceleme gerekli
      </span>
    </div>
    <v-alert
      v-if="brandError"
      type="warning"
      outlined
      dense
    >
      {{ brandError }}
      <v-btn
        small
        text
        :loading="brandsLoading"
        @click="loadBrands"
      >
        Tekrar dene
      </v-btn>
    </v-alert>
    <v-alert
      v-if="error"
      type="error"
      outlined
      dense
    >
      {{ error }}
    </v-alert>
    <v-alert
      v-if="result"
      type="info"
      outlined
      dense
    >
      {{ result }}
    </v-alert>
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="visibleRows"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :show-select="status === 'PENDING'"
      item-key="id"
      selectable-key="selectable"
      :footer-props="{ itemsPerPageOptions: [25, 50] }"
      no-data-text="Kayıt bulunamadı"
      @update:options="load"
    >
      <template v-slot:header.data-table-select="{ props, on }">
        <input
          type="checkbox"
          aria-label="Bu sayfadaki bekleyen kayıtları seç"
          :checked="props.value"
          :indeterminate.prop="props.indeterminate"
          :disabled="!visibleRows.some(row => row.selectable) || busy || loading"
          @change="on.input($event.target.checked)"
        >
      </template>
      <template v-slot:item.data-table-select="{ item, isSelected, select }">
        <input
          type="checkbox"
          :aria-label="`${item.brandName} ${item.form.name} seç`"
          :checked="isSelected"
          :disabled="!item.selectable || busy || loading"
          @change="select($event.target.checked)"
        >
      </template>
      <template v-slot:item.image="{ item }">
        <img
          v-if="image(item)"
          :src="image(item)"
          :alt="item.brandName"
          class="candidate-thumb"
        >
      </template>
      <template v-slot:item.name="{ item }">
        <strong>{{ item.form.name }}</strong><div class="text-caption">
          {{ item.brandName }}
        </div>
      </template>
      <template v-slot:item.kind="{ item }">
        {{ item.form.campaignKind === 'COLLECTION' ? 'Koleksiyon' : 'Kampanya' }}
      </template>
      <template v-slot:item.createdAt="{ item }">
        <time :datetime="dateTime(item.createdAt)">{{ formatAdminDate(item.createdAt) }}</time>
      </template>
      <template v-slot:item.ready="{ item }">
        <v-chip
          small
          label
          :color="item.ready ? 'success' : 'warning'"
        >
          {{ item.status === 'APPROVED' ? 'Onaylandı' : item.status === 'REJECTED' ? 'Reddedildi' : item.ready ? 'Yayına hazır' : 'İnceleme gerekli' }}
        </v-chip>
        <div
          v-if="item.status === 'PENDING' && !item.ready"
          class="text-caption mt-1"
          :title="reviewReasons(item).join(' · ')"
        >
          {{ reviewReasons(item).slice(0, 2).join(' · ') }}
        </div>
      </template>
      <template v-slot:item.activation="{ item }">
        {{ item.form.activationDate || 'Onay sonrası' }}
      </template>
      <template v-slot:item.date="{ item }">
        {{ item.form.endDate || 'Belirsiz' }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          small
          text
          color="primary"
          :disabled="busy"
          @click="$emit('review', item)"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiPencil }}
          </v-icon>{{ item.status === 'PENDING' ? 'İncele ve düzenle' : 'Görüntüle' }}
        </v-btn>
      </template>
    </v-data-table>
    <v-dialog
      v-model="confirm"
      max-width="480"
      :persistent="busy"
    >
      <v-card>
        <v-card-title>Toplu yayın onayı</v-card-title>
        <v-card-text>
          {{ approvalRows.length }} kampanya/koleksiyon yayınlanacak. Gelecek aktif olma tarihi olanlar planlanacak.
          <div
            v-if="selected.length > approvalRows.length"
            class="mt-3 warning--text"
          >
            İncelemesi tamamlanmamış {{ selected.length - approvalRows.length }} kayıt yayınlanmayacak.
          </div>
          <div
            v-if="announcementCount"
            class="mt-3 error--text"
          >
            {{ announcementCount }} kayıtta bildirim veya e-posta açık.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn
            text
            :disabled="busy"
            @click="confirm = false"
          >
            Vazgeç
          </v-btn><v-btn
            color="primary"
            :loading="busy"
            :disabled="!approvalRows.length || loading"
            @click="approve"
          >
            Onayla ve yayınla
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import axios from 'axios'
import { mdiRefresh, mdiPencil, mdiCheckAll } from '@mdi/js'
import { getAccessToken } from '@/utils/panelSession'
import { resolveApiErrorMessage } from '@/utils/apiErrors'
import { formatAdminDate, parseAdminDate } from '@/utils/adminDate'

export default {
  name: 'CampaignCandidateQueue',
  data: () => ({
    icons: { mdiRefresh, mdiPencil, mdiCheckAll },
    rows: [],
    selected: [],
    total: 0,
    search: '',
    offerRefId: null,
    brands: [],
    brandsLoading: false,
    brandError: '',
    brandRequestId: 0,
    status: 'PENDING',
    readyOnly: false,
    loading: false,
    busy: false,
    confirm: false,
    error: '',
    result: '',
    timer: null,
    requestId: 0,
    options: { page: 1, itemsPerPage: 25 },
    statuses: [{ text: 'Onay bekleyenler', value: 'PENDING' }, { text: 'Onaylananlar', value: 'APPROVED' }, { text: 'Reddedilenler', value: 'REJECTED' }],
    headers: [{ text: 'Görsel', value: 'image', sortable: false }, { text: 'Kampanya', value: 'name', sortable: false }, { text: 'Tip', value: 'kind', sortable: false }, { text: 'Eklenme (Türkiye)', value: 'createdAt', sortable: false }, { text: 'Aktif olma (Türkiye)', value: 'activation', sortable: false }, { text: 'Bitiş (Türkiye)', value: 'date', sortable: false }, { text: 'Durum', value: 'ready', sortable: false }, { text: '', value: 'actions', sortable: false }],
  }),
  computed: {
    visibleRows() {
      const rows = this.readyOnly ? this.rows.filter(row => row.ready) : this.rows

      return rows.map(row => ({ ...row, selectable: row.status === 'PENDING' }))
    },
    approvalRows() { return this.status === 'PENDING' ? this.selected.filter(row => row.status === 'PENDING' && row.ready) : [] },
    announcementCount() { return this.approvalRows.filter(row => row.form.sendEmail || row.form.sendPushNotification).length },
  },
  mounted() { this.loadBrands() },
  beforeDestroy() { clearTimeout(this.timer); this.requestId += 1; this.brandRequestId += 1 },
  methods: {
    formatAdminDate,
    dateTime(value) {
      const date = parseAdminDate(value)

      return date ? date.toISOString() : undefined
    },
    statusChanged() { this.offerRefId = null; this.brands = []; this.loadBrands(); this.reset() },
    reload() { this.loadBrands(); this.load() },
    async loadBrands() {
      this.brandRequestId += 1
      const requestId = this.brandRequestId
      this.brandsLoading = true
      this.brandError = ''
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/campaign-candidates/brands`, {
          ...this.auth(), params: { status: this.status },
        })
        if (requestId !== this.brandRequestId) return
        this.brands = response.data.data
      } catch (error) {
        if (requestId !== this.brandRequestId) return
        this.brandError = resolveApiErrorMessage(error, 'Marka filtresi yüklenemedi.')
      } finally { if (requestId === this.brandRequestId) this.brandsLoading = false }
    },
    reviewReasons(item) {
      const reasons = []
      if (!item.sourceReviewed) reasons.push('Kaynak kontrolü bekliyor')
      if (!item.form.categoryId?.length) reasons.push('Kategori eksik')

      return [...new Set([...reasons, ...(item.errors || [])])]
    },
    auth() { return { headers: { Authorization: `Bearer ${getAccessToken()}` } } },
    image(row) {
      const doc = new DOMParser().parseFromString(row.form.description || '', 'text/html')
      const url = doc.querySelector('img')?.getAttribute('src') || ''

      return url.startsWith('https://cdn.adverport.com/adverportaffiliate/campaigns/') ? url : ''
    },
    searchLater() { clearTimeout(this.timer); this.timer = setTimeout(this.reset, 350) },
    reset() { clearTimeout(this.timer); this.options.page = 1; this.selected = []; this.load() },
    async load() {
      this.requestId += 1
      const { requestId } = this
      this.loading = true
      this.error = ''
      this.selected = []
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/campaign-candidates`, {
          ...this.auth(),
          params: {
            status: this.status,
            search: this.search || '',
            page: this.options.page - 1,
            size: this.options.itemsPerPage,
            offerRefId: this.offerRefId || undefined,
          },
        })
        if (requestId !== this.requestId) return
        this.rows = response.data.data.content
        this.total = response.data.data.totalElements
      } catch (error) {
        if (requestId !== this.requestId) return
        this.error = resolveApiErrorMessage(error, 'Adaylar yüklenemedi.')
        this.rows = []
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    async approve() {
      if (this.busy || this.loading || !this.approvalRows.length) return
      this.busy = true
      this.error = ''
      try {
        const response = await axios.post(`${process.env.VUE_APP_BASE_API_URL}admin/campaign-candidates/approve-batch`, {
          items: this.approvalRows.map(row => ({ id: row.id, version: row.version })),
        }, this.auth())
        const outcomes = response.data.data
        const failures = outcomes.filter(row => !row.success)
        this.result = `${outcomes.length - failures.length} kayıt onaylandı.${failures.length ? ` ${failures.length} kayıt yayınlanmadı: ${failures.map(row => row.error).join(' ')}` : ''}`
        this.confirm = false
        await this.load()
        this.$emit('published')
      } catch (error) {
        const message = resolveApiErrorMessage(error, 'İşlem sonucu alınamadı. Yenileyip kayıtları kontrol edin.')
        this.confirm = false
        await this.load()
        this.error = message
      } finally { this.busy = false }
    },
  },
}
</script>

<style scoped>
.candidate-queue { padding: 16px 20px; }
.candidate-queue__toolbar { display: flex; gap: 12px; align-items: center; margin-bottom: 16px; flex-wrap: wrap; }
.candidate-queue__toolbar .v-input { max-width: 320px; min-width: 180px; }
.candidate-thumb { width: 132px; height: 64px; object-fit: contain; display: block; }
@media (max-width: 600px) { .candidate-queue { padding: 12px; } .candidate-queue__toolbar .v-input { max-width: none; width: 100%; } }
</style>
